import { UniDriver } from '@wix/wix-ui-test-utils/dist/types/unidriver';
import { baseUniDriverFactory } from '../utils/test-utils/unidriver';

export const tabsUniDriverFactory = (base: UniDriver) => {
  const findFirst = async (query: string): Promise<UniDriver | null> => {
    const item = base.$$(query).get(0);
    return (await item.exists()) ? item : null;
  };
  const getItemsContainer = async (): Promise<UniDriver | null> =>
    findFirst('div[role="tablist"]');
  const getItems = async (): Promise<UniDriver[]> => {
    const itemsContainer = await getItemsContainer();
    const tabs =
      itemsContainer &&
      ((await itemsContainer.getAll('button[role="tab"]')) as unknown);
    // @ts-expect-error
    return tabs?.map(child => child) as UniDriver[];
  };

  const getItemsContainerType = async () => {
    const itemContainer = await getItemsContainer();
    return (await itemContainer?.attr('data-type')) as string;
  };

  return {
    ...baseUniDriverFactory(base),

    /**
     * Gets all titles text
     * @return {Promise<string[]>}
     */
    getTitles: async () =>
      Promise.all((await getItems()).map(item => item.text())),

    /**
     * Clicks the tab at index
     * @param {number} index
     * @return {Promise<void>}
     */
    clickTabAt: async (index: number) => (await getItems())[index].click(),

    /**
     * Gets active tab index
     * @return {Promise<number>}
     */
    getActiveTabIndex: async () => {
      const itemsDataActivePromises = (await getItems()).map(item =>
        item.attr('data-active'),
      );
      const itemsDataActive = await Promise.all(itemsDataActivePromises);
      return itemsDataActive.findIndex(active => active === 'true');
    },

    /**
     * Checks whether tabs type is default
     * @return {Promise<boolean>}
     */
    isDefaultType: async () => !(await getItemsContainerType()),

    /**
     * Gets tabs container type
     * @return {Promise<string>}
     */
    getItemsContainerType,

    /**
     * Gets the data-hook
     * @param {number} index
     * @return {Promise<string>}
     */
    getDataHook: async (index: number) =>
      (await getItems())[index].attr('data-hook') as Promise<string>,

    /**
     * Gets a set of tabs widths
     * @return {Promise<Set<string>>}
     */
    getItemsWidth: async () => {
      const items = await getItems();
      const itemsWidthArrayPromise = items.map(item =>
        item._prop('style').then((style: CSSStyleDeclaration) => style.width),
      );
      const itemsWidthArray = await Promise.all(itemsWidthArrayPromise);
      return new Set(itemsWidthArray);
    },

    /**
     * Checks whether tabs has a divider
     * @return {Promise<boolean>}
     */
    hasDivider: async () => (await base.attr('data-divider')) === 'true',

    /**
     * Gets the side content
     * @return {Promise<UniDriver|null>}
     */
    getSideContent: async () => findFirst(`[data-content="true"]`),

    /**
     * Gets tabs max widths
     * @return {Promise<string[]>}
     */
    getItemsMaxWidths: async () =>
      Promise.all(
        (await getItems()).map(item =>
          item
            ._prop('style')
            .then((style: CSSStyleDeclaration) => style.maxWidth),
        ),
      ),

    /**
     * Gets tabs min widths
     * @return {Promise<string[]>}
     */
    getItemsMinWidths: async () =>
      Promise.all(
        (await getItems()).map(item =>
          item
            ._prop('style')
            .then((style: CSSStyleDeclaration) => style.minWidth),
        ),
      ),
  };
};

export type TabsUniDriver = ReturnType<typeof tabsUniDriverFactory>;
